import type { WidgetProps } from '@rjsf/utils';
import { useCallback } from 'react';
import SubPriceComponent, { type SubPrice } from './components/sub-price';
import RadioWidget from './radio-widget';

const DEFAULT_SUBPRICE = [
  { pricingFormula: undefined, employeeContribution: 50 },
];

interface PricingWidgetProps extends Omit<WidgetProps, 'value'> {
  value: { showPrice: boolean; subPrices: SubPrice[] } | undefined;
}

const PricingWidget = ({
  value,
  onChange,
  schema,
  readonly,
}: PricingWidgetProps) => {
  const properties = schema.properties.subPrices.items.properties;
  const showPrice = schema.properties.showPrice;
  const subPrices = value?.subPrices?.length
    ? value?.subPrices
    : DEFAULT_SUBPRICE;

  const handleUpdateSubPrice = useCallback(
    (index: number, key: string, newValue: number | string) => {
      const updatedSubprices = subPrices.map((subPrice, subPriceIndex) => {
        if (subPriceIndex === index) {
          return {
            ...subPrice,
            [key]: newValue,
          };
        }
        return subPrice;
      });
      onChange({
        ...value,
        subPrices: updatedSubprices,
      });
    },
    [onChange, subPrices, value]
  );

  const onChangePricingFormula = useCallback(
    (pricingFormula: string, index: number) => {
      handleUpdateSubPrice(index, 'pricingFormula', pricingFormula.toString());
    },
    [handleUpdateSubPrice]
  );

  const onChangeEmployeeContribution = useCallback(
    (employeeContribution: number, index: number) => {
      handleUpdateSubPrice(index, 'employeeContribution', employeeContribution);
    },
    [handleUpdateSubPrice]
  );

  return (
    <>
      {subPrices.map((subPrice: SubPrice, index: number) => {
        return (
          <SubPriceComponent
            key={index}
            subPrice={subPrice}
            properties={properties}
            readonly={readonly}
            index={index}
            onChangeEmployeeContribution={onChangeEmployeeContribution}
            onChangePricingFormula={onChangePricingFormula}
          />
        );
      })}
      <RadioWidget
        label={showPrice?.title}
        value={subPrices[0].pricingFormula ? value?.showPrice : false}
        onChange={(value: boolean) => onChange({ subPrices, showPrice: value })}
        disabled={
          showPrice?.disabled || !subPrices[0].pricingFormula || readonly
        }
        schema={showPrice}
      />
    </>
  );
};

export default PricingWidget;
